import type { Dispatch, SetStateAction } from 'react'
import React, { useState, useEffect } from 'react'

import CancelIcon from '@mui/icons-material/Cancel'
import { CircularProgress, FormHelperText } from '@mui/material'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select from '@mui/material/Select'
import { useAuthContext } from 'AuthProvider'
import { useTransportType } from 'hooks/useTransportType'
import _without from 'lodash/without'

import type { SelectChangeEvent } from '@mui/material/Select';
import type { GetStockStockGetRequest } from 'api-client'
import type { MstTransportTypeSeq, MstTransportTypesSeq} from 'hooks/useTransportType';

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
}

interface SelectTransportTypeProps {
  searchParam: GetStockStockGetRequest
  trCompanyId: number | null | undefined
  transportTypeId?: object | null | undefined
  isRequired: boolean
  setTransportTypeIDList: Dispatch<SetStateAction<any | null | undefined>>
  setTransportTypeInfoList: Dispatch<SetStateAction<boolean | null | undefined>>
  setIsValid: Dispatch<SetStateAction<boolean>>
  enabled?: boolean
}

export default function SelectTransportType({
  trCompanyId,
  searchParam,
  transportTypeId,
  setTransportTypeIDList,
  setTransportTypeInfoList,
  isRequired,
  setIsValid,
  enabled,
}: SelectTransportTypeProps) {
  const { loginUserInfo } = useAuthContext()
  const { transportTypeSearch, fetchTransportTypeSearch, isLoading } = useTransportType()
  const [isFirstRender, setIsFirstRender] = useState(true)
  const [transportType, setTransportType] = useState<MstTransportTypesSeq | undefined>(undefined)
  const [selectedTransportIds, setSelectedTransportIds] = useState<string[]>([])

  useEffect(() => {
    if (!isFirstRender) {
      setTransportTypeIDList(undefined)
    }

    if (trCompanyId === undefined || trCompanyId === null || trCompanyId === loginUserInfo?.companyId) {
      fetchTransportTypeSearch({ enabled: enabled ?? undefined, companyId: loginUserInfo!.companyId })
    } else {
      fetchTransportTypeSearch({ enabled: enabled ?? undefined, companyId: trCompanyId })
    }
  }, [trCompanyId])

  useEffect(() => {
    if (transportTypeSearch.list.length) {
      setTransportType(transportTypeSearch)
    }

    if (isFirstRender) {
      setIsFirstRender(false)
    }
  })

  useEffect(()=> {
    if(selectedTransportIds.length > 0) {
      setIsValid(true)
    }else {
      setIsValid(false)
    }
  }, [selectedTransportIds])

  const getSelectedTransportObjects = (ids: string[]) =>
    transportType?.list.filter((item) => ids.includes(String(item.transportTypeId ?? ''))) ?? []

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value
    setSelectedTransportIds(value)

    const selected = getSelectedTransportObjects(value)
    setTransportTypeIDList(selected)
    setTransportTypeInfoList(true)
    setIsValid(!isRequired || selected.length > 0)
  }

  const handleDelete = (e: React.MouseEvent, value: string) => {
    e.preventDefault()

    const newSelected = _without(selectedTransportIds, value)
    setSelectedTransportIds(newSelected)

    const selected = getSelectedTransportObjects(newSelected)
    setTransportTypeIDList(selected)
    setTransportTypeInfoList(true)
    setIsValid(!isRequired || selected.length > 0)
  }

  useEffect(() => {
    if(transportTypeId === undefined) {
      setSelectedTransportIds([])
    } else if (selectedTransportIds.length === 0) {
      const listTransportTypeId: string[] = []
      const transportTypes = transportTypeId as MstTransportTypeSeq[]

      transportTypes?.forEach((item) => {
        if (item?.transportTypeId !== undefined && item?.transportTypeId !== null) {
          listTransportTypeId.push(String(item.transportTypeId))
        }
      })

      setSelectedTransportIds(listTransportTypeId)

      const selected = getSelectedTransportObjects(listTransportTypeId)
      setTransportTypeInfoList(true)
      setIsValid(!isRequired || selected.length > 0)
    }
  }, [transportTypeId])

  const availableTransportList = transportType?.list.filter((item) => item.deleteFlg === false) ?? []

  return (
    <FormControl
      sx={{ mr: 1, width: 600 }}
      disabled={isLoading}
      error={isRequired && selectedTransportIds.length === 0}
    >
      <InputLabel id="demo-multiple-chip-label">使用車両</InputLabel>
      <Select
        labelId="demo-multiple-chip-label"
        id="demo-multiple-chip"
        multiple
        value={selectedTransportIds}
        onChange={handleChange}
        sx={{ overflow: 'hidden' }}
        input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
        renderValue={(selected) => (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              width: 550,
              overflowY: 'auto',
              maxHeight: 30,
            }}
          >
            {selected.map((value) => {
              const item = transportType?.list.find((i) => String(i.transportTypeId ?? '') === value)
              if (!item) return null

              return (
                <Chip
                  key={value}
                  label={`[${item.transportTypeId ?? ''}]${item.transportTypeName}(${item.maxWeight}t,${item.maxPalletCount}PL)`}
                  size="small"
                  deleteIcon={<CancelIcon />}
                  onDelete={(e) => handleDelete(e, value)}
                  onMouseDown={(event) => event.stopPropagation()}
                />
              )
            })}
          </Box>
        )}
        MenuProps={MenuProps}
      >
        {isLoading ? (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              読み込み中...
            </Box>
          </MenuItem>
        ) : (
          availableTransportList?.map((item) => (
            <MenuItem key={item.transportTypeId ?? ''} value={String(item.transportTypeId ?? '')}>
              [{item.transportTypeId ?? ''}]{item.transportTypeName ?? ''} ({item.maxWeight}t,{item.maxPalletCount}PL)
            </MenuItem>
          ))
        )}
      </Select>
      {isRequired && selectedTransportIds.length === 0 && (
        <FormHelperText>使用車両を選択してください</FormHelperText>
      )}
    </FormControl>
  )
}
